import React from "react";
import { Show, EditButton, DeleteButton } from "@refinedev/mui";
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
} from "@mui/material";
import { useShow, IResourceComponentsProps } from "@refinedev/core";
import { format } from "date-fns";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

interface IPremiumDeviceShow {
  id: number;
  macAddress: string;
  serialNumber?: string;
  description?: string;
  validityDays: number;
  isActive: boolean;
  activatedAt?: Date;
  activatedByUserId?: number;
  user?: {
    id: number;
    email: string;
    fullname?: string;
    isPremium: boolean;
    premiumExpiresAt?: Date;
  };
}

export const PremiumDeviceShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<IPremiumDeviceShow>();
  const { data, isLoading } = queryResult;
  const record = data?.data;

  const calculateDuration = (days: number) => {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    let duration = "";
    if (years > 0) duration += `${years} year${years > 1 ? "s" : ""}`;
    if (months > 0)
      duration += ` ${months} month${months > 1 ? "s" : ""}`;
    return duration.trim() || `${days} days`;
  };

  return (
    <Show isLoading={isLoading} headerButtons={({ defaultButtons, editButtonProps, deleteButtonProps }) => (
      <>
        {defaultButtons}
      </>
    )}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Device Status */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Device Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Box mt={1}>
                  {record?.isActive ? (
                    <Chip
                      label="Active"
                      size="medium"
                      color="success"
                      icon={<CheckCircleIcon />}
                    />
                  ) : (
                    <Chip
                      label="Inactive"
                      size="medium"
                      color="error"
                      icon={<CancelIcon />}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Activation Status
                </Typography>
                <Box mt={1}>
                  {record?.activatedByUserId ? (
                    <Chip
                      label="Activated"
                      size="medium"
                      color="primary"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label="Not Activated"
                      size="medium"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Device Information */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Device Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  MAC Address
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {record?.macAddress}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Serial Number
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {record?.serialNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {record?.description || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Validity Duration
                </Typography>
                <Typography variant="body1">
                  {record?.validityDays} days ({calculateDuration(record?.validityDays || 0)})
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Activation Information */}
        {record?.activatedByUserId && record?.user && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activation Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Activated At
                  </Typography>
                  <Typography variant="body1">
                    {record.activatedAt
                      ? format(new Date(record.activatedAt), "PPpp")
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    User Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">
                    {record.user.fullname || record.user.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {record.user.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Premium Status
                  </Typography>
                  <Box mt={1}>
                    {record.user.isPremium ? (
                      <Chip
                        label="Premium Active"
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Chip
                        label="Not Premium"
                        size="small"
                        color="default"
                      />
                    )}
                  </Box>
                </Grid>
                {record.user.premiumExpiresAt && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Premium Expires At
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(record.user.premiumExpiresAt), "PPpp")}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>
    </Show>
  );
};
